import type { MeResponse } from "@keydials/api-client";

/**
 * The trial gate, as seen from the client (KEY-637).
 *
 * The server closes a card-free account whose trial has run out by answering 402 on every gated route — and it
 * does so from wherever the person happens to be, not only at sign-in. Before this, only sign-in, My Dials and
 * the builder caught that 402; someone already inside a dial when it happened saw the server's sentence as a
 * plain error with no button. The api client now announces every 402 here, and the auth provider listens: it
 * flags the session as gated and re-reads /api/me, and App.tsx renders the trial-ended screen off the flag,
 * above every route. No page needs to catch the 402 itself any more.
 *
 * A plain listener set rather than React state so the api client — a module-level singleton, created before
 * any provider mounts — can call it, and so it can be exercised without rendering anything.
 *
 * Since KEY-911 the same gate also closes a CANCELLED subscription, and the 402 says which with a `code`
 * extension. The listener is told the reason, so the ended screen can be worded right at once — before the
 * /api/me re-read that confirms it has come back.
 */

/** Why the app has been closed: a card-free trial that ran out, or a subscription that was cancelled (KEY-911). */
export type AccessEndedReason = "trial" | "cancelled";

/** The 402's `code` in the app's terms. Null when the server sent none — /api/me's `endedReason` decides then. */
export function reasonFromGateCode(code: string | null | undefined): AccessEndedReason | null {
  switch (code) {
    case "trial-ended":
      return "trial";
    case "subscription-ended":
      return "cancelled";
    default:
      return null;
  }
}

type Listener = (reason: AccessEndedReason | null) => void;

const listeners = new Set<Listener>();

/** Registers a listener for the gate. Returns the way to unregister it. */
export function onTrialGate(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** What the api client calls on a 402. Safe with nobody listening — a request before the provider mounts. */
export function trialGateHit(_path?: string, code?: string | null): void {
  const reason = reasonFromGateCode(code);
  for (const listener of listeners) {
    listener(reason);
  }
}

/** Whether /api/me says this account's access has ended, for either reason (KEY-911). */
export function hasEnded(me: Pick<MeResponse, "subscriptionEnded" | "trialExpired"> | null | undefined): boolean {
  return !!me && (!!me.subscriptionEnded || !!me.trialExpired);
}

/**
 * Why access ended, for wording the screen: /api/me's own answer first, then the 402's code (a gate met mid-session,
 * before the re-read lands), then "trial" — the only reason there was before KEY-911, so a server or a 402 that
 * names none keeps the wording it always had.
 */
export function endedReasonOf(
  me: Pick<MeResponse, "subscriptionEnded" | "trialExpired" | "endedReason"> | null | undefined,
  gatedReason: AccessEndedReason | null,
): AccessEndedReason | null {
  if (me?.endedReason) return me.endedReason;
  if (gatedReason) return gatedReason;
  return hasEnded(me) ? "trial" : null;
}
